import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import InstagramBookmarkIcon from '@src/svgs/InstagramBookmarkIcon';
import InstagramCommentIcon from '@src/svgs/InstagramCommentIcon';
import InstagramHeartIcon from '@src/svgs/InstagramHeartIcon';
import InstagramShareIcon from '@src/svgs/InstagramShareIcon';
import MusicNoteIcon from '@src/svgs/MusicNoteIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import SearchIcon from '@src/svgs/SearchIcon';
import React from 'react';
import styled from 'styled-components';

import {
  BrandHandle,
  BrandLogo,
  BrandName,
  StoryDescription,
} from '../BrandSlots';
import { useChromeFontSize } from '../useChromeFontSize';
import { OverlayFrame } from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.tiktokShortFormVideo;

/**
 * TikTok overlay — paints TikTok's full-screen For You vertical viewer chrome
 * over the clip-preview rect.
 *
 * Top strip: centered three-tab row ("Live | Following | **For You**") with
 * `For You` bolded + underlined as the active tab, plus a right-anchored
 * search glyph.
 *
 * Right rail (top → bottom): profile avatar (rounded-square) with a red `+`
 * follow badge hanging at the bottom-center, like, comment, bookmark, share,
 * and a rotating sound disc at the bottom. TikTok paints the brand picture
 * on the *rail* rather than in the bottom strip, so the bottom area is
 * just a username row + description + music attribution.
 *
 * Bottom strip: brand row (`@username` + red-outlined Follow pill, no
 * avatar) + 2-line clamped description preview with `...more` pinned right +
 * sound attribution row (`MusicNoteIcon` + "Original sound · {brand}",
 * single-line ellipsized — no marquee scroll in Phase 1).
 *
 * Captions block (when `showCaptions`): centered white-on-black highlighted
 * text, sized to match Instagram Reels / LinkedIn Shorts.
 *
 * Scrubber: always-on thin `0.25em` line at the very bottom driving
 * `SafeZonePlaybackBridge` via the shared `useScrubberDrag` hook. No thumb
 * dot, matching the other Shorts overlays.
 *
 * 9:16 only. Non-9:16 uploads (1:1 / 4:5) are out of scope today; TikTok
 * does center those with letterbox bands on the real surface, but the
 * synthetic-viewport plumbing for that was removed when 1:1 was pulled
 * during the LinkedIn Shorts build.
 *
 * Scaling matches the other Shorts overlays so all four read at the same
 * visual weight on identical rects: chrome sized in `em` against a base
 * font-size derived from `min(rect.width / 360, rect.height / 640)`,
 * clamped `[0.05, 3]`.
 */
const REFERENCE_RECT_WIDTH = 360;
const REFERENCE_RECT_HEIGHT = 640;
const REFERENCE_FONT_PX = 10;
const SCALE_FLOOR = 0.05;
const SCALE_MAX = 3;

// TikTok's signature brand red — used for the follow `+` badge on the rail
// avatar and the Follow pill's outline in the bottom strip.
const TIKTOK_RED = '#fe2c55';

export const TikTokOverlay: React.FC<SafeZoneProps> = ({ rect, playback }) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_RECT_WIDTH,
    referenceHeight: REFERENCE_RECT_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_FLOOR,
    scaleMax: SCALE_MAX,
  });

  // Always-on bottom scrubber — same shared hook every Shorts overlay uses
  // so seek behavior stays in lockstep across the vertical short-form
  // family.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);
  const duration = playback?.duration ?? 0;

  return (
    // Frame fills the video rect (9:16). All chrome positions resolve
    // against the Frame's bounds.
    <OverlayFrame
      data-testid="tiktok-overlay"
      $fontFamily="'Helvetica Neue', Arial, sans-serif"
      style={{ fontSize: `${fontPx}px` }}
    >
      <TopStrip>
        <TopTabs>
          <TopTabInactive aria-label={COPY.aria.liveTab}>
            {COPY.tabs.live}
          </TopTabInactive>
          <TopTabInactive aria-label={COPY.aria.followingTab}>
            {COPY.tabs.following}
          </TopTabInactive>
          <TopTabActiveWrap>
            <TopTabActive aria-label={COPY.aria.forYouTab}>
              {COPY.tabs.forYou}
            </TopTabActive>
            <TopTabActiveUnderline aria-hidden="true" />
          </TopTabActiveWrap>
        </TopTabs>
        <TopSearchBtn type="button" aria-label={COPY.aria.search}>
          <SearchIcon width="2.2em" height="2.2em" fillColor="#ffffff" />
        </TopSearchBtn>
      </TopStrip>

      <RightRail>
        {/* Profile avatar with red `+` follow badge — TikTok's signature
            top-of-rail affordance. The avatar is the brand logo cropped to
            a rounded-square; the badge hangs at the bottom-center via
            `position: absolute`. */}
        <ProfileItem>
          <ProfileAvatarBtn type="button" aria-label={COPY.aria.profile}>
            <ProfileAvatarInner>
              <BrandLogo />
            </ProfileAvatarInner>
            <FollowBadge aria-label={COPY.aria.follow}>
              <PlusIcon width="1em" height="1em" strokeColor="#ffffff" />
            </FollowBadge>
          </ProfileAvatarBtn>
        </ProfileItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.like}>
            <InstagramHeartIcon
              width="2.8em"
              height="2.8em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.like}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.comment}>
            <InstagramCommentIcon
              width="2.8em"
              height="2.8em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.comment}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.bookmark}>
            <InstagramBookmarkIcon
              width="2.6em"
              height="2.6em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.bookmark}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.share}>
            <InstagramShareIcon
              width="2.6em"
              height="2.6em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.share}</ActionCount>
        </ActionItem>
      </RightRail>

      {/* Stationary music-icon circle — positioned to align with the sound
          row in the bottom strip. No rotation and no brand logo: TikTok's
          real surface DOES spin an album-art disc here, but the captionless
          chrome reads cleaner with a single static glyph, and skipping the
          rotation avoids a CSS animation running for a visual the user
          watches for seconds at a time. */}
      <SoundDiscBtn type="button" aria-label={COPY.aria.sound}>
        <SoundDiscRing>
          <MusicNoteIcon width="1.6em" height="1.6em" fillColor="#ffffff" />
        </SoundDiscRing>
      </SoundDiscBtn>

      <BottomStrip>
        <BrandRow>
          <BrandUsername>
            <BrandHandle />
          </BrandUsername>
          <FollowPill type="button" aria-label={COPY.aria.follow}>
            {COPY.followLabel}
          </FollowPill>
        </BrandRow>

        <CaptionPreview>
          <StoryDescription fallback={COPY.descriptionFallback} />
          {/*
            "...more" pinned to the right of the description row. Same
            pattern Instagram Reels uses — `font-size: inherit` so it
            tracks the description's scaled font.
          */}
          <MoreLink>{COPY.moreLink}</MoreLink>
        </CaptionPreview>

        <SoundRow>
          <SoundIconWrap>
            <MusicNoteIcon width="1.2em" height="1.2em" fillColor="#ffffff" />
          </SoundIconWrap>
          <SoundText>
            {COPY.soundFallback} · <BrandName />
          </SoundText>
        </SoundRow>
      </BottomStrip>

      {/* Always-on thin scrubber at the very bottom — no thumb dot.
          Pointer-events activate when the playback bridge is ready,
          matching the other Shorts overlays' rail behavior. */}
      <ScrubberRail
        ref={scrubberRef}
        role="slider"
        aria-label={COPY.aria.seek}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, duration)}
        aria-valuenow={Math.max(0, displayTime)}
        onMouseDown={onScrubberMouseDown}
        $interactive={scrubberInteractive}
      >
        <ScrubberFill style={{ width: `${progressPct}%` }} />
      </ScrubberRail>
    </OverlayFrame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 360×640 reference).
// ---------------------------------------------------------------------------

// Top strip — centered three-tab cluster with search glyph anchored right.
// Like Instagram Reels' top strip, the edge button is absolute-positioned
// so its width doesn't nudge the centered cluster off-center.
const TopStrip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5em;
  padding: 1.4em 1.4em 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const TopTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4em;
`;

const TopTabInactive = styled.span`
  font-size: 1.6em;
  font-weight: 600;
  letter-spacing: 0.005em;
  color: rgba(255, 255, 255, 0.65);
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

// Active tab is paired with an underline rendered as a sibling — wrapping
// them in a flex column so the underline pins directly under the tab text
// regardless of the tab's intrinsic width.
const TopTabActiveWrap = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25em;
`;

const TopTabActive = styled.span`
  font-size: 1.6em;
  font-weight: 700;
  letter-spacing: 0.005em;
  color: #ffffff;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

const TopTabActiveUnderline = styled.span`
  width: 1.4em;
  height: 0.15em;
  background: #ffffff;
  border-radius: 0.08em;
`;

const TopSearchBtn = styled.button`
  position: absolute;
  top: 1.4em;
  right: 1.4em;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Right-side action rail. Bottom-anchored above the bottom strip's three-
// row layout. `z-index: 1` lifts it above the bottom strip's gradient
// backdrop so the rail's white icons stay legible where they overlap the
// strip's text.
const RightRail = styled.div`
  position: absolute;
  right: 0.8em;
  bottom: 5.8em;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6em;
`;

// Profile avatar item — wraps the avatar button so its bottom-centered
// follow badge has room to extend below the avatar without being clipped
// by the rail's flex gap. No count below.
const ProfileItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Reserve vertical room below for the follow badge that overhangs the
     avatar bottom edge — without this, the badge can collide with the
     next rail item under tight em-scaling. */
  padding-bottom: 0.6em;
`;

const ProfileAvatarBtn = styled.button`
  position: relative;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4em;
  height: 4em;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

const ProfileAvatarInner = styled.span`
  display: inline-block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 0.18em #ffffff inset;
`;

// Red `+` badge hanging at the bottom-center of the avatar — TikTok's
// signature follow affordance.
const FollowBadge = styled.span`
  position: absolute;
  left: 50%;
  bottom: -0.45em;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  border-radius: 50%;
  background: ${TIKTOK_RED};
  border: 0.15em solid rgba(0, 0, 0, 0.05);
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
`;

// Thicken every stroked path inside the rail's icons so the rail reads
// uniformly across glyphs with different intrinsic stroke widths. Assumes
// the icon paints its outline via a `stroke` (every icon currently used
// here does). Fill-only glyphs have no `stroke` attribute, so the override
// harmlessly no-ops on them.
const ActionIconBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  margin: -0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));

  & svg path,
  & svg circle,
  & svg rect {
    stroke-width: 2;
  }
`;

const ActionCount = styled.span`
  font-size: 1.1em;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

// Stationary sound-icon circle — small dark disc holding just a music-note
// glyph. Sits at the right edge, vertically aligned with the bottom strip's
// sound row, so it reads as the "audio source" affordance attached to the
// "Original sound · {brand}" label rather than as a separate rail item.
//
// `bottom` matches the bottom strip's bottom-padding (1.4em) — the sound
// row is the lowest text row in the strip and sits ~1em above the strip's
// inner edge, so a 1.4em `bottom` puts the disc's vertical center near the
// sound-row text baseline. `right` matches the right rail's `right: 0.8em`
// so the disc sits directly under the rail column.
const SoundDiscBtn = styled.button`
  position: absolute;
  right: 0.8em;
  bottom: 1.4em;
  z-index: 1;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Dark circular frame holding the music-note glyph — smaller than the
// profile-avatar tile (2.6em vs 4em) so it reads as a secondary affordance.
const SoundDiscRing = styled.span`
  display: inline-flex;
  width: 2.6em;
  height: 2.6em;
  border-radius: 50%;
  background: #1a1a1a;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 0.1em rgba(255, 255, 255, 0.6) inset;
`;

// Bottom strip — brand row + description preview + sound row. `bottom`
// matches the scrubber rail's height (0.25em) so the strip's lower edge
// sits flush against the scrubber's top edge. Padding right reserves
// clearance for the right rail so text never tucks underneath it.
//
// Lighter gradient than Instagram Reels — TikTok's bottom area uses soft
// text shadows on a transparent base rather than a full-width dark band.
const BottomStrip = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.25em;
  padding: 1.4em 5.5em 1.4em 1.6em;
  display: flex;
  flex-direction: column;
  gap: 0.7em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0.2) 60%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7em;
`;

const BrandUsername = styled.span`
  font-size: 1.3em;
  font-weight: 700;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// TikTok paints Follow as a thin red-outlined rounded square (white text,
// no fill) — distinct from Instagram Reels' translucent-white pill and
// LinkedIn Shorts' solid-white pill.
const FollowPill = styled.button`
  pointer-events: auto;
  flex: 0 0 auto;
  font-size: 1.05em;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.35em 0.95em;
  border: 0.12em solid ${TIKTOK_RED};
  border-radius: 0.4em;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(254, 44, 85, 0.18);
  }
`;

// `position: relative` so the absolutely-positioned `MoreLink` anchors to
// the description row. Same two-line clamp pattern Instagram Reels uses.
const CaptionPreview = styled.div`
  position: relative;
  font-size: 1.15em;
  font-weight: 400;
  line-height: 1.35;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  padding-right: 4em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const MoreLink = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: inherit;
  line-height: 1.35;
  opacity: 0.85;
`;

// Sound attribution row — music-note glyph + "Original sound · {brand}".
// Single-line, ellipsized. No marquee scroll in Phase 1.
const SoundRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 1.05em;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  min-width: 0;
`;

const SoundIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const SoundText = styled.span`
  display: inline-block;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Thin always-on scrubber line — matches the other Shorts overlays.
// Pointer-events flip to `auto` only when there's a real bridge to drive.
const ScrubberRail = styled.div<{ $interactive: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 0.25em;
  background: #5a5a5a;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ScrubberFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
`;
